import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';

import Skeleton from 'coreComponents/Skeleton';
import {useUI} from 'coreHooks';
import useGeolocalizedSizes from 'coreHooks/useGeolocalizedSizes';

import Money from 'brandComponents/Money';
import Picture from 'brandComponents/Picture';
import {PRODUCT_IMAGES_SETTINGS, ViewType} from 'brandConstants';

import {IProductDataProps} from './IProductData';
import messages from './messages';

import styles from './ProductData.module.scss';

const ProductData = ({
	item,
	product,
	quantity,
	currency,
	step,
	reasonToReturn,
	isExchange,
	shippingCountryCode,
	isBonus
}: IProductDataProps) => {
	const intl = useIntl();
	const isServerUI = useUI('isServer');
	const productId = product.getProductId();
	const productBrand = product.getBrand();
	const productName = product.getName();
	const productColor = product.getColorDescription();
	const geolocalizedSize = useGeolocalizedSizes(product, {countryCode: shippingCountryCode});
	const productImage = product.getProductImage({
		viewType: ViewType.ZOOM,
		transformationObject: PRODUCT_IMAGES_SETTINGS.myAccount,
	});

	const price = useMemo(() => {
		const priceAfterOrderDiscount = item.getPriceAfterOrderDiscount();
		const proratedPrice = item.getProratedPriceAdjustmentPrices();
		const hasProratedPrice = proratedPrice && proratedPrice !== item.getPrice();
		const hasPriceAfterDiscount = priceAfterOrderDiscount && priceAfterOrderDiscount !== item.getPrice();
		const hasAnOrderDiscount: boolean = hasPriceAfterDiscount || hasProratedPrice;
		let itemPrice;

		if (item.isExchanged()) {
			itemPrice = item.getOriginalExchangedPrice();
		} else if (hasAnOrderDiscount) {
			itemPrice = proratedPrice || priceAfterOrderDiscount;
		} else {
			itemPrice = item.getPriceAfterItemDiscount() ?? item.getPrice();
		}

		return itemPrice / item.getQuantity();
	}, [item.getProductId()]);

	if (!product) {
		return <Skeleton width="320px" height="133px" />;
	}

	const isProductRefund = () => {
		if (reasonToReturn) {
			return true;
		}

		return false;
	};

	// TODO: use the chunk to add the value next to the label
	return (
		<div className={styles['product-data']} key={item.getItemId()}>
			<div className={styles['product-data__image-wrapper']}>
				<Picture
					image={productImage}
					classNames={styles['product-data__image']}
					showSkeleton={!isServerUI}
				/>
			</div>
			<ul className={styles['product-data__list']}>
				<li className={styles['product-data__brand']}>{productBrand}</li>
				<li className={styles['product-data__detail']}>{productName}</li>
				{!isBonus &&
					<li className={styles['product-data__detail']}>
						{intl.formatMessage(messages.total)}
						<Money
							value={price}
							currency={currency}
							spaceBetweenCurrencyAndPrice={false}
						/>
					</li>
				}
				<li className={styles['product-data__detail']}>
					{intl.formatMessage(messages.quantity)}
					<span>{quantity || item.getQuantity()}</span>
				</li>
				<li className={styles['product-data__detail']}>
					{intl.formatMessage(messages.color)}
					<span className={styles['product-data__item--color']}>{productColor}</span>
				</li>
				<li className={styles['product-data__detail']}>
					{intl.formatMessage(messages.size)}
					<span>{geolocalizedSize[product.getProductId()]}</span>
				</li>
				<li className={styles['product-data__detail']}>
					{intl.formatMessage(messages.number)}
					<span>{productId}</span>
				</li>
				{step === 2 && (
					<li className={styles['product-data__label']}>
						{
							isProductRefund
								?
								!isExchange
									? <p className={styles['product-data__label--refund']}>
										{intl.formatMessage(messages.refundRequest)}</p>
									: <p className={styles['product-data__label--exchange']}>
										{intl.formatMessage(messages.exchangeRequest)}</p>
								: ''
						}
					</li>
				)}

				{reasonToReturn && (
					<li className="product-data__item--reason-to-return">
						{intl.formatMessage(messages.reasonToReturn)}:<span>{reasonToReturn}</span>
					</li>
				)}
			</ul>
		</div>
	);
};

export default ProductData;
